/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║          SKIPCAR — DESIGN SYSTEM MUI 5                      ║
 * ║          Version 1.0 · Avril 2026                           ║
 * ╚══════════════════════════════════════════════════════════════╝
 *
 * Usage :
 *   import { skipcarTheme } from './theme'
 *   <ThemeProvider theme={skipcarTheme}>
 *     <CssBaseline />
 *     <App />
 *   </ThemeProvider>
 *
 * Structure :
 *   1. Tokens bruts (palette, typo, radius, shadows)
 *   2. createTheme() avec palette + typography + shape
 *   3. Overrides composants (Button, Chip, Card, Table, Input…)
 *   4. Types augmentés (TypeScript)
 */

import { createTheme, alpha } from '@mui/material/styles'

// ─────────────────────────────────────────────────────────────
// 01. DESIGN TOKENS
// ─────────────────────────────────────────────────────────────

export const tokens = {
  // ── Couleurs de base ──────────────────────────────────────
  color: {
    // Brand
    yellow:         '#F5C200',
    yellowDark:     '#D4A800',
    yellowLight:    '#FFFBEB',
    yellowMuted:    '#FEF9C3',

    // Surfaces
    dark:           '#1C1C1E',
    darkMid:        '#252527',
    darkSurface:    '#2C2C2E',
    darkBorder:     '#3A3A3C',
    bg:             '#F0EFED',
    bgMid:          '#E8E7E5',
    white:          '#FFFFFF',
    surface:        '#FAFAF9',
    border:         '#E8E8E6',
    borderDark:     '#D4D4D2',

    // Sémantiques
    green:          '#22C55E',
    greenDark:      '#16A34A',
    greenDeep:      '#15803D',
    greenLight:     '#F0FFF4',
    greenBorder:    '#BBF7D0',

    red:            '#EF4444',
    redDark:        '#DC2626',
    redLight:       '#FFF5F5',
    redBorder:      '#FECACA',

    orange:         '#F97316',
    orangeDark:     '#EA580C',
    orangeLight:    '#FFF7ED',
    orangeBorder:   '#FDBA74',

    blue:           '#3B82F6',
    blueDark:       '#2563EB',
    blueLight:      '#EFF6FF',
    blueBorder:     '#BFDBFE',

    purple:         '#8B5CF6',
    purpleDark:     '#7C3AED',
    purpleLight:    '#F5F3FF',
    purpleBorder:   '#DDD6FE',

    amber:          '#F59E0B',
    amberDark:      '#D97706',
    amberDeep:      '#A16207',
    amberLight:     '#FEFCE8',
    amberBorder:    '#FDE68A',

    // Textes
    text:           '#1C1C1E',
    textSecondary:  '#666666',
    textMuted:      '#888888',
    textFaint:      '#BBBBBB',
    textDisabled:   '#CCCCCC',

    // Overlays
    overlay04:      'rgba(0,0,0,0.04)',
    overlay08:      'rgba(0,0,0,0.08)',
    overlay12:      'rgba(0,0,0,0.12)',
    overlay20:      'rgba(0,0,0,0.20)',
    overlayYellow:  'rgba(245,194,0,0.12)',
  },

  // ── Typographie ───────────────────────────────────────────
  font: {
    family: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    familyMono: '"JetBrains Mono", "SF Mono", "Cascadia Code", "Fira Code", monospace',

    // Scale
    xs:   '0.6875rem',  // 11px
    sm:   '0.75rem',    // 12px
    base: '0.8125rem',  // 13px
    md:   '0.875rem',   // 14px
    lg:   '1rem',       // 16px
    xl:   '1.125rem',   // 18px
    '2xl':'1.25rem',    // 20px
    '3xl':'1.5rem',     // 24px
    '4xl':'1.875rem',   // 30px
    '5xl':'2.25rem',    // 36px
    '6xl':'3rem',       // 48px

    // Weights
    regular: 400,
    medium:  500,
    semibold:600,
    bold:    700,
    extrabold:800,
    black:   900,

    // Line heights
    tight:  1.2,
    snug:   1.35,
    normal: 1.5,
    relaxed:1.65,
    loose:  1.8,

    // Letter spacings
    tighter: '-0.03em',
    tight_:  '-0.01em',
    normal_: '0',
    wide:    '0.04em',
    wider:   '0.08em',
    widest:  '0.14em',
  },

  // ── Radius ────────────────────────────────────────────────
  radius: {
    none:  0,
    xs:    2,
    sm:    4,
    md:    6,
    lg:    8,
    xl:    10,
    '2xl': 12,
    '3xl': 16,
    full:  9999,
  },

  // ── Spacing (base = 4px) ──────────────────────────────────
  space: {
    1:  '4px',
    2:  '8px',
    3:  '12px',
    4:  '16px',
    5:  '20px',
    6:  '24px',
    8:  '32px',
    10: '40px',
    12: '48px',
    16: '64px',
  },

  // ── Shadows ───────────────────────────────────────────────
  shadow: {
    none: 'none',
    xs:   '0 1px 2px rgba(0,0,0,0.04)',
    sm:   '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    md:   '0 4px 6px rgba(0,0,0,0.05), 0 2px 4px rgba(0,0,0,0.04)',
    lg:   '0 10px 15px rgba(0,0,0,0.06), 0 4px 6px rgba(0,0,0,0.04)',
    xl:   '0 20px 25px rgba(0,0,0,0.08), 0 10px 10px rgba(0,0,0,0.03)',
    yellow: '0 4px 14px rgba(245,194,0,0.25)',
    card:   '0 0 0 0.5px #E8E8E6, 0 1px 3px rgba(0,0,0,0.06)',
    focus:  '0 0 0 3px rgba(245,194,0,0.35)',
    focusRed: '0 0 0 3px rgba(239,68,68,0.25)',
  },

  // ── Z-index ───────────────────────────────────────────────
  z: {
    base:    0,
    raised:  10,
    overlay: 100,
    modal:   1000,
    toast:   1100,
    tooltip: 1200,
  },

  // ── Transitions ───────────────────────────────────────────
  motion: {
    fast:   '100ms ease',
    normal: '150ms ease',
    slow:   '200ms ease',
    spring: '200ms cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
}

// ─────────────────────────────────────────────────────────────
// 02. PALETTE MUI
// ─────────────────────────────────────────────────────────────

const palette = {
  mode: 'light' as const,

  primary: {
    main:         tokens.color.yellow,
    light:        tokens.color.yellowMuted,
    dark:         tokens.color.yellowDark,
    contrastText: tokens.color.dark,
  },

  secondary: {
    main:         tokens.color.dark,
    light:        tokens.color.darkMid,
    dark:         '#111113',
    contrastText: tokens.color.white,
  },

  success: {
    main:         tokens.color.green,
    light:        tokens.color.greenLight,
    dark:         tokens.color.greenDark,
    contrastText: tokens.color.white,
  },

  error: {
    main:         tokens.color.red,
    light:        tokens.color.redLight,
    dark:         tokens.color.redDark,
    contrastText: tokens.color.white,
  },

  warning: {
    main:         tokens.color.orange,
    light:        tokens.color.orangeLight,
    dark:         tokens.color.orangeDark,
    contrastText: tokens.color.white,
  },

  info: {
    main:         tokens.color.blue,
    light:        tokens.color.blueLight,
    dark:         tokens.color.blueDark,
    contrastText: tokens.color.white,
  },

  // Couleurs personnalisées — accessibles via theme.palette.brand etc.
  // (voir augmentation TypeScript en bas de fichier)
  brand: {
    yellow:       tokens.color.yellow,
    yellowDark:   tokens.color.yellowDark,
    yellowLight:  tokens.color.yellowLight,
    dark:         tokens.color.dark,
    mid:          tokens.color.darkMid,
    surface:      tokens.color.darkSurface,
  },

  status: {
    draft:        tokens.color.textFaint,
    pending:      tokens.color.blue,
    toVerify:     tokens.color.amberDeep,
    critical:     tokens.color.orangeDark,
    blocked:      tokens.color.redDark,
    submitted:    tokens.color.purpleDark,
    delivered:    tokens.color.greenDeep,
    autoValidated:tokens.color.greenDark,
  },

  grey: {
    50:  '#FAFAF9',
    100: '#F5F5F4',
    200: '#E8E8E6',
    300: '#D4D4D2',
    400: '#AAAAAA',
    500: '#888888',
    600: '#666666',
    700: '#444444',
    800: '#252527',
    900: '#1C1C1E',
  },

  text: {
    primary:   tokens.color.text,
    secondary: tokens.color.textSecondary,
    disabled:  tokens.color.textDisabled,
  },

  background: {
    default: tokens.color.bg,
    paper:   tokens.color.white,
  },

  divider: tokens.color.border,

  action: {
    hover:            alpha(tokens.color.dark, 0.04),
    hoverOpacity:     0.04,
    selected:         alpha(tokens.color.yellow, 0.08),
    selectedOpacity:  0.08,
    disabled:         tokens.color.textDisabled,
    disabledBackground: tokens.color.bg,
    focus:            alpha(tokens.color.yellow, 0.12),
    focusOpacity:     0.12,
  },
}

// ─────────────────────────────────────────────────────────────
// 03. TYPOGRAPHY
// ─────────────────────────────────────────────────────────────

const typography = {
  fontFamily: tokens.font.family,
  fontSize:   13,
  fontWeightLight:   tokens.font.regular,
  fontWeightRegular: tokens.font.regular,
  fontWeightMedium:  tokens.font.medium,
  fontWeightBold:    tokens.font.bold,

  // Display — titres de pages, covers
  h1: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font['6xl'],   // 48px
    fontWeight:    tokens.font.black,
    lineHeight:    tokens.font.tight,
    letterSpacing: tokens.font.tighter,
    color:         tokens.color.dark,
  },

  // Section headers
  h2: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font['5xl'],   // 36px
    fontWeight:    tokens.font.extrabold,
    lineHeight:    tokens.font.tight,
    letterSpacing: tokens.font.tighter,
    color:         tokens.color.dark,
  },

  h3: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font['4xl'],   // 30px
    fontWeight:    tokens.font.bold,
    lineHeight:    tokens.font.snug,
    letterSpacing: tokens.font.tight_,
    color:         tokens.color.dark,
  },

  // Card titles, section labels
  h4: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font['3xl'],   // 24px
    fontWeight:    tokens.font.bold,
    lineHeight:    tokens.font.snug,
    letterSpacing: tokens.font.tight_,
    color:         tokens.color.dark,
  },

  h5: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font['2xl'],   // 20px
    fontWeight:    tokens.font.semibold,
    lineHeight:    tokens.font.snug,
    letterSpacing: '0',
    color:         tokens.color.dark,
  },

  h6: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.xl,       // 18px
    fontWeight:    tokens.font.semibold,
    lineHeight:    tokens.font.normal,
    letterSpacing: '0',
    color:         tokens.color.dark,
  },

  // Subtitles
  subtitle1: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.md,       // 14px
    fontWeight:    tokens.font.semibold,
    lineHeight:    tokens.font.normal,
    color:         tokens.color.textSecondary,
  },

  subtitle2: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.base,     // 13px
    fontWeight:    tokens.font.medium,
    lineHeight:    tokens.font.normal,
    color:         tokens.color.textMuted,
  },

  // Corps de texte
  body1: {
    fontFamily:  tokens.font.family,
    fontSize:    tokens.font.base,       // 13px
    fontWeight:  tokens.font.regular,
    lineHeight:  tokens.font.relaxed,
    color:       tokens.color.text,
  },

  body2: {
    fontFamily:  tokens.font.family,
    fontSize:    tokens.font.sm,         // 12px
    fontWeight:  tokens.font.regular,
    lineHeight:  tokens.font.normal,
    color:       tokens.color.textSecondary,
  },

  // Petits textes UI
  caption: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.xs,       // 11px
    fontWeight:    tokens.font.regular,
    lineHeight:    tokens.font.normal,
    color:         tokens.color.textMuted,
    letterSpacing: tokens.font.wide,
  },

  // Labels champs, étiquettes
  overline: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.xs,       // 11px
    fontWeight:    tokens.font.bold,
    lineHeight:    tokens.font.normal,
    letterSpacing: tokens.font.widest,
    textTransform: 'uppercase' as const,
    color:         tokens.color.textFaint,
  },

  // Boutons
  button: {
    fontFamily:    tokens.font.family,
    fontSize:      tokens.font.base,     // 13px
    fontWeight:    tokens.font.semibold,
    lineHeight:    '1',
    letterSpacing: tokens.font.wide,
    textTransform: 'none' as const,
  },
}

// ─────────────────────────────────────────────────────────────
// 04. BASE THEME (sans overrides)
// ─────────────────────────────────────────────────────────────

const baseTheme = createTheme({
  palette,
  typography,

  shape: {
    borderRadius: tokens.radius.lg,  // 8px — base
  },

  spacing: 4,  // 1 unit = 4px → MUI spacing(2) = 8px

  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },

  shadows: [
    'none',                                                          // 0
    tokens.shadow.xs,                                                // 1
    tokens.shadow.sm,                                                // 2
    tokens.shadow.md,                                                // 3
    tokens.shadow.lg,                                                // 4
    tokens.shadow.xl,                                                // 5
    tokens.shadow.card,                                              // 6
    tokens.shadow.yellow,                                            // 7
    // 8-24 restants = neutres
    ...Array(17).fill('0 1px 3px rgba(0,0,0,0.06)'),
  ] as any,

  transitions: {
    duration: {
      shortest:  100,
      shorter:   150,
      short:     200,
      standard:  250,
      complex:   375,
      enteringScreen: 225,
      leavingScreen:  195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut:   'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn:    'cubic-bezier(0.4, 0, 1, 1)',
      sharp:     'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  zIndex: {
    mobileStepper: 1000,
    fab:           1050,
    speedDial:     1050,
    appBar:        1100,
    drawer:        1200,
    modal:         1300,
    snackbar:      1400,
    tooltip:       1500,
  },
})

// ─────────────────────────────────────────────────────────────
// 05. COMPONENT OVERRIDES
// ─────────────────────────────────────────────────────────────

export const skipcarTheme = createTheme(baseTheme, {
  components: {

    // ── CssBaseline ────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        body: {
          backgroundColor: tokens.color.bg,
          color: tokens.color.text,
          lineHeight: tokens.font.relaxed,
        },
        'input:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 100px ${tokens.color.white} inset`,
          WebkitTextFillColor: tokens.color.dark,
        },
        ':focus-visible': {
          outline: `2px solid ${tokens.color.yellow}`,
          outlineOffset: '2px',
        },
        '::selection': {
          backgroundColor: alpha(tokens.color.yellow, 0.2),
          color: tokens.color.dark,
        },
        // Scrollbar (Chrome/Edge)
        '::-webkit-scrollbar': { width: '6px', height: '6px' },
        '::-webkit-scrollbar-track': { background: 'transparent' },
        '::-webkit-scrollbar-thumb': {
          background: tokens.color.border,
          borderRadius: '3px',
          '&:hover': { background: tokens.color.textFaint },
        },
      },
    },

    // ── Button ─────────────────────────────────────────────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          fontFamily:    tokens.font.family,
          fontWeight:    tokens.font.semibold,
          fontSize:      tokens.font.base,
          letterSpacing: tokens.font.wide,
          textTransform: 'none',
          borderRadius:  tokens.radius.lg,
          transition:    `all ${tokens.motion.normal}`,
          lineHeight:    1,

          '&:focus-visible': {
            boxShadow: tokens.shadow.focus,
          },
        },

        // Tailles
        sizeSmall: {
          height: '28px',
          padding: '0 10px',
          fontSize: tokens.font.sm,
          borderRadius: tokens.radius.md,
        },
        sizeMedium: {
          height: '34px',
          padding: '0 14px',
        },
        sizeLarge: {
          height: '40px',
          padding: '0 20px',
          fontSize: tokens.font.md,
        },

        // Contained primary = fond jaune
        containedPrimary: {
          backgroundColor: tokens.color.yellow,
          color:           tokens.color.dark,
          '&:hover': {
            backgroundColor: tokens.color.yellowDark,
            boxShadow:       tokens.shadow.yellow,
          },
          '&:active': {
            backgroundColor: '#C49600',
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.color.yellowMuted,
            color:           alpha(tokens.color.dark, 0.4),
          },
        },

        // Contained secondary = fond dark
        containedSecondary: {
          backgroundColor: tokens.color.dark,
          color:           tokens.color.white,
          '&:hover': {
            backgroundColor: tokens.color.darkMid,
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.color.border,
            color:           tokens.color.textDisabled,
          },
        },

        // Outlined
        outlinedPrimary: {
          borderColor: tokens.color.yellow,
          color:       tokens.color.dark,
          '&:hover': {
            backgroundColor: tokens.color.yellowLight,
            borderColor:     tokens.color.yellowDark,
          },
        },

        outlinedSecondary: {
          borderColor: tokens.color.border,
          color:       tokens.color.text,
          '&:hover': {
            backgroundColor: tokens.color.surface,
            borderColor:     tokens.color.borderDark,
          },
        },

        // Text
        textPrimary: {
          color: tokens.color.dark,
          '&:hover': {
            backgroundColor: alpha(tokens.color.yellow, 0.08),
          },
        },

        // Danger (custom via sx ou variant override)
        containedError: {
          backgroundColor: tokens.color.redLight,
          color:           tokens.color.redDark,
          border:          `0.5px solid ${tokens.color.redBorder}`,
          '&:hover': {
            backgroundColor: tokens.color.redBorder,
          },
        },
      },

      // Variants personnalisés
      variants: [
        // btn-ghost = outlined neutre
        {
          props: { variant: 'outlined', color: 'inherit' },
          style: {
            borderColor:     tokens.color.border,
            color:           tokens.color.textSecondary,
            backgroundColor: tokens.color.white,
            '&:hover': {
              backgroundColor: tokens.color.surface,
              borderColor:     tokens.color.borderDark,
            },
          },
        },
        // btn-danger-soft
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: tokens.color.redLight,
            color:           tokens.color.redDark,
            boxShadow:       'none',
            border:          `0.5px solid ${tokens.color.redBorder}`,
            '&:hover': {
              backgroundColor: '#FECACA',
              boxShadow:       'none',
            },
          },
        },
      ],
    },

    // ── IconButton ─────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius:    tokens.radius.lg,
          transition:      `all ${tokens.motion.fast}`,
          color:           tokens.color.textMuted,
          '&:hover': {
            backgroundColor: tokens.color.surface,
            color:           tokens.color.dark,
          },
          '&:focus-visible': {
            boxShadow: tokens.shadow.focus,
          },
        },
        sizeSmall: {
          padding: '4px',
        },
        sizeMedium: {
          padding: '6px',
        },
        sizeLarge: {
          padding: '8px',
        },
      },
    },

    // ── Chip ───────────────────────────────────────────────
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontFamily:    tokens.font.family,
          fontWeight:    tokens.font.bold,
          fontSize:      tokens.font.xs,
          letterSpacing: tokens.font.wider,
          textTransform: 'uppercase',
          height:        '22px',
          borderRadius:  tokens.radius.full,
          transition:    `all ${tokens.motion.fast}`,

          // Dot avant le label
          '& .MuiChip-label': {
            paddingLeft:  '7px',
            paddingRight: '8px',
            display:      'flex',
            alignItems:   'center',
            gap:          '5px',
          },
        },

        // Couleurs par défaut (status tags)
        colorDefault: {
          backgroundColor: '#F5F5F4',
          color:           tokens.color.textMuted,
        },

        colorPrimary: {
          backgroundColor: tokens.color.amberLight,
          color:           tokens.color.amberDeep,
          border:          `0.5px solid ${tokens.color.amberBorder}`,
        },

        colorSuccess: {
          backgroundColor: tokens.color.greenLight,
          color:           tokens.color.greenDeep,
          border:          `0.5px solid ${tokens.color.greenBorder}`,
        },

        colorError: {
          backgroundColor: tokens.color.redLight,
          color:           tokens.color.redDark,
          border:          `0.5px solid ${tokens.color.redBorder}`,
        },

        colorWarning: {
          backgroundColor: tokens.color.orangeLight,
          color:           tokens.color.orangeDark,
          border:          `0.5px solid ${tokens.color.orangeBorder}`,
        },

        colorInfo: {
          backgroundColor: tokens.color.blueLight,
          color:           tokens.color.blueDark,
          border:          `0.5px solid ${tokens.color.blueBorder}`,
        },

        colorSecondary: {
          backgroundColor: tokens.color.purpleLight,
          color:           tokens.color.purpleDark,
          border:          `0.5px solid ${tokens.color.purpleBorder}`,
        },
      },
    },

    // ── Card ───────────────────────────────────────────────
    MuiCard: {
      defaultProps: {
        elevation: 6,  // = tokens.shadow.card
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.white,
          borderRadius:    tokens.radius.xl,
          border:          `0.5px solid ${tokens.color.border}`,
          boxShadow:       tokens.shadow.card,
          overflow:        'hidden',
          transition:      `box-shadow ${tokens.motion.normal}`,
          '&:hover': {
            boxShadow: tokens.shadow.md,
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px',
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '14px 16px 0',
        },
        title: {
          fontSize:   tokens.font.base,
          fontWeight: tokens.font.bold,
          color:      tokens.color.dark,
        },
        subheader: {
          fontSize: tokens.font.sm,
          color:    tokens.color.textMuted,
        },
      },
    },

    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          borderTop: `0.5px solid ${tokens.color.border}`,
        },
      },
    },

    // ── Paper ──────────────────────────────────────────────
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: tokens.radius.xl,
        },
        outlined: {
          border: `0.5px solid ${tokens.color.border}`,
        },
        elevation1: {
          boxShadow: tokens.shadow.xs,
          border:    `0.5px solid ${tokens.color.border}`,
        },
        elevation2: {
          boxShadow: tokens.shadow.sm,
        },
        elevation3: {
          boxShadow: tokens.shadow.md,
        },
      },
    },

    // ── TextField & Input ──────────────────────────────────
    MuiTextField: {
      defaultProps: {
        variant:  'outlined',
        size:     'small',
        fullWidth: true,
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontFamily:      tokens.font.family,
          fontSize:        tokens.font.base,
          backgroundColor: tokens.color.white,
          borderRadius:    tokens.radius.lg,
          transition:      `all ${tokens.motion.fast}`,

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.border,
            borderWidth: '0.5px',
            transition:  `border-color ${tokens.motion.fast}`,
          },

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.borderDark,
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.yellow,
            borderWidth: '1.5px',
            boxShadow:   tokens.shadow.focus,
          },

          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.color.red,
          },
          '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
            boxShadow: tokens.shadow.focusRed,
          },

          '&.Mui-disabled': {
            backgroundColor: tokens.color.surface,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.color.border,
            },
          },
        },

        input: {
          padding:     '7px 12px',
          color:       tokens.color.dark,
          '&::placeholder': {
            color:   tokens.color.textFaint,
            opacity: 1,
          },
        },

        inputSizeSmall: {
          padding: '6px 10px',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.base,
          color:      tokens.color.textMuted,
          transform:  'translate(12px, 7px) scale(1)',

          '&.Mui-focused': {
            color: tokens.color.yellowDark,
          },
          '&.Mui-error': {
            color: tokens.color.red,
          },
        },
        shrink: {
          transform: 'translate(12px, -8px) scale(0.85)',
        },
        sizeSmall: {
          transform: 'translate(10px, 6px) scale(1)',
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontFamily:  tokens.font.family,
          fontSize:    tokens.font.xs,
          marginLeft:  '2px',
          marginTop:   '4px',
          color:       tokens.color.textMuted,
          '&.Mui-error': {
            color: tokens.color.redDark,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '7px 12px',
          fontSize: tokens.font.base,
        },
        icon: {
          color: tokens.color.textMuted,
          right: '8px',
        },
      },
    },

    // ── Table ──────────────────────────────────────────────
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing:  0,
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.xl,
          border:       `0.5px solid ${tokens.color.border}`,
          overflow:     'hidden',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: tokens.color.surface,
            borderBottom:    `0.5px solid ${tokens.color.border}`,
            fontFamily:      tokens.font.family,
            fontSize:        tokens.font.xs,
            fontWeight:      tokens.font.bold,
            color:           tokens.color.textFaint,
            letterSpacing:   tokens.font.widest,
            textTransform:   'uppercase',
            padding:         '10px 14px',
            whiteSpace:      'nowrap',
          },
        },
      },
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            transition: `background-color ${tokens.motion.fast}`,
            '&:hover': {
              backgroundColor: '#FAFAF8',
            },
          },
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily:  tokens.font.family,
          fontSize:    tokens.font.sm,
          color:       tokens.color.dark,
          padding:     '10px 14px',
          borderBottom:`0.5px solid #F5F5F4`,
          verticalAlign:'middle',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: alpha(tokens.color.yellow, 0.06),
            '&:hover': {
              backgroundColor: alpha(tokens.color.yellow, 0.1),
            },
          },
          '&.row-blocked': {
            backgroundColor: '#FFF9F9',
          },
          '&.row-warning': {
            backgroundColor: '#FFFDF0',
          },
        },
      },
    },

    // ── Alert ──────────────────────────────────────────────
    MuiAlert: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.base,
          borderRadius: tokens.radius.lg,
          padding:      '10px 14px',
          alignItems:   'flex-start',
        },

        // Standard (outlined soft)
        standardInfo: {
          backgroundColor: tokens.color.blueLight,
          color:           tokens.color.blueDark,
          border:          `0.5px solid ${tokens.color.blueBorder}`,
          '& .MuiAlert-icon': { color: tokens.color.blue },
        },

        standardSuccess: {
          backgroundColor: tokens.color.greenLight,
          color:           tokens.color.greenDeep,
          border:          `0.5px solid ${tokens.color.greenBorder}`,
          '& .MuiAlert-icon': { color: tokens.color.green },
        },

        standardWarning: {
          backgroundColor: tokens.color.amberLight,
          color:           tokens.color.amberDeep,
          border:          `0.5px solid ${tokens.color.amberBorder}`,
          '& .MuiAlert-icon': { color: tokens.color.amber },
        },

        standardError: {
          backgroundColor: tokens.color.redLight,
          color:           tokens.color.redDark,
          border:          `0.5px solid ${tokens.color.redBorder}`,
          '& .MuiAlert-icon': { color: tokens.color.red },
        },

        // Filled dark
        filled: {
          '&.MuiAlert-filledInfo': {
            backgroundColor: tokens.color.dark,
            color:           tokens.color.white,
          },
        },

        message: {
          padding:    '1px 0 0',
          lineHeight: tokens.font.relaxed,
        },

        icon: {
          padding:    '1px 0 0',
          marginRight:'10px',
          fontSize:   '18px',
          opacity:    1,
        },
      },
    },

    // ── Snackbar / Toast ───────────────────────────────────
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        autoHideDuration: 4000,
      },
    },

    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.dark,
          color:           tokens.color.white,
          fontFamily:      tokens.font.family,
          fontSize:        tokens.font.base,
          fontWeight:      tokens.font.medium,
          borderRadius:    tokens.radius.lg,
          padding:         '10px 14px',
          boxShadow:       tokens.shadow.lg,
        },
        message: {
          padding: '0',
        },
      },
    },

    // ── Dialog ─────────────────────────────────────────────
    MuiDialog: {
      defaultProps: {
        PaperProps: {
          elevation: 0,
        },
      },
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius['2xl'],
          border:       `0.5px solid ${tokens.color.border}`,
          boxShadow:    tokens.shadow.xl,
          minWidth:     '400px',
        },
        paperWidthSm: { maxWidth: '480px' },
        paperWidthMd: { maxWidth: '640px' },
        paperWidthLg: { maxWidth: '800px' },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily:  tokens.font.family,
          fontSize:    tokens.font.xl,
          fontWeight:  tokens.font.bold,
          color:       tokens.color.dark,
          padding:     '20px 24px 12px',
          borderBottom:`0.5px solid ${tokens.color.border}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          fontSize: tokens.font.base,
          color:    tokens.color.textSecondary,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding:     '12px 20px',
          borderTop:   `0.5px solid ${tokens.color.border}`,
          gap:         '8px',
        },
      },
    },

    // ── Drawer ─────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.color.dark,
          color:           tokens.color.white,
          borderRight:     `1px solid ${tokens.color.darkBorder}`,
          backgroundImage: 'none',
        },
        paperAnchorRight: {
          backgroundColor: tokens.color.white,
          borderLeft:      `0.5px solid ${tokens.color.border}`,
          borderRight:     'none',
        },
      },
    },

    // ── AppBar ─────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'transparent',
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.dark,
          borderBottom:    `0.5px solid ${tokens.color.darkBorder}`,
          backgroundImage: 'none',
        },
        colorDefault: {
          backgroundColor: tokens.color.white,
          borderBottom:    `0.5px solid ${tokens.color.border}`,
          color:           tokens.color.dark,
        },
      },
    },

    // ── Toolbar ────────────────────────────────────────────
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important',
          padding:   '0 20px !important',
        },
      },
    },

    // ── Tabs ───────────────────────────────────────────────
    MuiTabs: {
      defaultProps: {
        indicatorColor: 'primary',
        textColor:      'inherit',
      },
      styleOverrides: {
        root: {
          minHeight: '36px',
          backgroundColor: tokens.color.bg,
          borderRadius:    tokens.radius.lg,
          padding:         '3px',
          display:         'inline-flex',
          width:           'fit-content',
        },
        indicator: {
          height:      '100%',
          borderRadius:tokens.radius.md,
          backgroundColor: tokens.color.white,
          boxShadow:   tokens.shadow.xs,
          zIndex:      0,
        },
        flexContainer: {
          position: 'relative',
          zIndex:   1,
          gap:      '2px',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily:    tokens.font.family,
          fontSize:      tokens.font.sm,
          fontWeight:    tokens.font.semibold,
          color:         tokens.color.textMuted,
          textTransform: 'none',
          minHeight:     '30px',
          padding:       '5px 14px',
          borderRadius:  tokens.radius.md,
          zIndex:        1,
          transition:    `color ${tokens.motion.fast}`,
          '&.Mui-selected': {
            color: tokens.color.dark,
          },
        },
      },
    },

    // ── Menu ───────────────────────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.xl,
          border:       `0.5px solid ${tokens.color.border}`,
          boxShadow:    tokens.shadow.lg,
          minWidth:     '180px',
          padding:      '4px',
        },
        list: {
          padding: '4px',
          gap:     '1px',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.base,
          color:        tokens.color.dark,
          borderRadius: tokens.radius.md,
          padding:      '7px 10px',
          gap:          '8px',
          minHeight:    'unset',
          transition:   `background-color ${tokens.motion.fast}`,

          '&:hover': {
            backgroundColor: tokens.color.surface,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(tokens.color.yellow, 0.08),
            fontWeight:      tokens.font.semibold,
            '&:hover': {
              backgroundColor: alpha(tokens.color.yellow, 0.12),
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: tokens.color.surface,
          },
          // Danger item
          '&.item-danger': {
            color: tokens.color.redDark,
            '&:hover': {
              backgroundColor: tokens.color.redLight,
            },
          },
        },
      },
    },

    // ── List ───────────────────────────────────────────────
    MuiList: {
      styleOverrides: {
        root: {
          padding: '0',
        },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '0',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.sm,
          fontWeight:   tokens.font.medium,
          color:        '#666666',
          borderRadius: tokens.radius.md,
          padding:      '7px 10px',
          margin:       '1px 6px',
          gap:          '9px',
          transition:   `all ${tokens.motion.fast}`,

          '&:hover': {
            backgroundColor: tokens.color.darkMid,
            color:           tokens.color.white,
          },

          '&.Mui-selected': {
            backgroundColor: tokens.color.darkMid,
            color:           tokens.color.white,
            '&:hover': {
              backgroundColor: '#2C2C2E',
            },
          },
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.sm,
          fontWeight: tokens.font.medium,
          lineHeight: 1.4,
        },
        secondary: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.xs,
          color:      tokens.color.textFaint,
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color:      'inherit',
          minWidth:   '32px',
          opacity:    0.7,
        },
      },
    },

    // ── Divider ────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.color.border,
          borderWidth: '0.5px',
        },
      },
    },

    // ── Avatar ─────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontFamily:  tokens.font.family,
          fontWeight:  tokens.font.bold,
          fontSize:    tokens.font.sm,
          width:       '28px',
          height:      '28px',
          borderRadius:tokens.radius.full,
        },
        colorDefault: {
          backgroundColor: tokens.color.yellow,
          color:           tokens.color.dark,
        },
      },
    },

    // ── Badge ──────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontFamily:  tokens.font.family,
          fontSize:    '9px',
          fontWeight:  tokens.font.bold,
          minWidth:    '16px',
          height:      '16px',
          padding:     '0 4px',
          borderRadius:tokens.radius.full,
        },
        colorPrimary: {
          backgroundColor: tokens.color.yellow,
          color:           tokens.color.dark,
        },
        colorError: {
          backgroundColor: '#3D1A1A',
          color:           tokens.color.red,
        },
      },
    },

    // ── Tooltip ────────────────────────────────────────────
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        placement: 'top',
      },
      styleOverrides: {
        tooltip: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.xs,
          fontWeight:   tokens.font.medium,
          backgroundColor: tokens.color.dark,
          color:           tokens.color.white,
          borderRadius: tokens.radius.md,
          padding:      '5px 8px',
          boxShadow:    tokens.shadow.md,
          maxWidth:     '280px',
          lineHeight:   tokens.font.normal,
        },
        arrow: {
          color: tokens.color.dark,
        },
      },
    },

    // ── LinearProgress ─────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height:      '4px',
          borderRadius:tokens.radius.full,
          backgroundColor: tokens.color.bg,
          overflow:    'hidden',
        },
        bar: {
          borderRadius: tokens.radius.full,
          transition:   `transform 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
        },
        colorPrimary: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: tokens.color.yellow,
          },
        },
        colorSuccess: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: tokens.color.green,
          },
        },
        colorError: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: tokens.color.red,
          },
        },
        colorWarning: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: tokens.color.orange,
          },
        },
        colorInfo: {
          '& .MuiLinearProgress-bar': {
            backgroundColor: tokens.color.blue,
          },
        },
      },
    },

    // ── CircularProgress ───────────────────────────────────
    MuiCircularProgress: {
      defaultProps: {
        size: 20,
        thickness: 3.5,
      },
      styleOverrides: {
        colorPrimary: {
          color: tokens.color.yellow,
        },
      },
    },

    // ── Skeleton ───────────────────────────────────────────
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
      styleOverrides: {
        root: {
          backgroundColor:    alpha(tokens.color.dark, 0.06),
          borderRadius:       tokens.radius.md,
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${alpha(tokens.color.dark, 0.04)}, transparent)`,
          },
        },
        rectangular: {
          borderRadius: tokens.radius.md,
        },
        rounded: {
          borderRadius: tokens.radius.lg,
        },
      },
    },

    // ── Switch ─────────────────────────────────────────────
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width:  36,
          height: 20,
          padding: 0,
          display:'flex',
        },
        switchBase: {
          padding: '2px',
          '&.Mui-checked': {
            transform:    'translateX(16px)',
            color:        tokens.color.dark,
            '& + .MuiSwitch-track': {
              backgroundColor: tokens.color.yellow,
              opacity:         1,
              border:          'none',
            },
          },
        },
        thumb: {
          width:        16,
          height:       16,
          borderRadius: tokens.radius.full,
          boxShadow:    tokens.shadow.xs,
        },
        track: {
          borderRadius:    tokens.radius.full,
          backgroundColor: tokens.color.border,
          opacity:         1,
          transition:      `background-color ${tokens.motion.normal}`,
        },
      },
    },

    // ── Checkbox ───────────────────────────────────────────
    MuiCheckbox: {
      defaultProps: {
        disableRipple: false,
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding:    '4px',
          color:      tokens.color.border,
          borderRadius: tokens.radius.sm,
          '&.Mui-checked': {
            color: tokens.color.yellow,
          },
          '&:hover': {
            backgroundColor: alpha(tokens.color.yellow, 0.06),
          },
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
      },
    },

    // ── Radio ──────────────────────────────────────────────
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding: '4px',
          color:   tokens.color.border,
          '&.Mui-checked': {
            color: tokens.color.yellow,
          },
          '& .MuiSvgIcon-root': {
            fontSize: '18px',
          },
        },
      },
    },

    // ── Accordion ──────────────────────────────────────────
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.white,
          border:          `0.5px solid ${tokens.color.border}`,
          borderRadius:    `${tokens.radius.lg}px !important`,
          overflow:        'hidden',
          marginBottom:    '4px',
          '&:before':      { display: 'none' },
          '&.Mui-expanded': {
            margin:  '0 0 4px',
            boxShadow: tokens.shadow.sm,
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding:    '0 14px',
          minHeight:  '42px',
          transition: `background-color ${tokens.motion.fast}`,
          '&:hover': {
            backgroundColor: tokens.color.surface,
          },
          '&.Mui-expanded': {
            minHeight:     '42px',
            borderBottom:  `0.5px solid ${tokens.color.border}`,
          },
        },
        content: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.base,
          fontWeight: tokens.font.semibold,
          color:      tokens.color.dark,
          margin:     '10px 0',
        },
        expandIconWrapper: {
          color: tokens.color.textMuted,
        },
      },
    },

    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '12px 14px',
        },
      },
    },

    // ── Popover ────────────────────────────────────────────
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.xl,
          border:       `0.5px solid ${tokens.color.border}`,
          boxShadow:    tokens.shadow.lg,
        },
      },
    },

    // ── Breadcrumbs ────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.sm,
        },
        separator: {
          color:   tokens.color.textFaint,
          margin:  '0 4px',
        },
        ol: {
          alignItems: 'center',
        },
      },
    },

    // ── Pagination ─────────────────────────────────────────
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.sm,
          fontWeight:   tokens.font.medium,
          borderRadius: tokens.radius.md,
          color:        tokens.color.textSecondary,
          border:       `0.5px solid ${tokens.color.border}`,
          height:       '28px',
          minWidth:     '28px',
          transition:   `all ${tokens.motion.fast}`,

          '&:hover': {
            backgroundColor: tokens.color.surface,
          },
          '&.Mui-selected': {
            backgroundColor: tokens.color.yellow,
            borderColor:     tokens.color.yellow,
            color:           tokens.color.dark,
            fontWeight:      tokens.font.bold,
            '&:hover': {
              backgroundColor: tokens.color.yellowDark,
            },
          },
        },
      },
    },

    // ── FormControl ────────────────────────────────────────
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },

    // ── FormLabel ──────────────────────────────────────────
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.sm,
          fontWeight: tokens.font.medium,
          color:      tokens.color.textSecondary,
          '&.Mui-focused': {
            color: tokens.color.yellowDark,
          },
        },
      },
    },

    // ── Autocomplete ───────────────────────────────────────
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.xl,
          border:       `0.5px solid ${tokens.color.border}`,
          boxShadow:    tokens.shadow.lg,
          marginTop:    '4px',
        },
        listbox: {
          padding: '4px',
        },
        option: {
          fontFamily:   tokens.font.family,
          fontSize:     tokens.font.base,
          borderRadius: tokens.radius.md,
          padding:      '7px 10px',
          '&[aria-selected="true"]': {
            backgroundColor: alpha(tokens.color.yellow, 0.08),
          },
          '&.Mui-focused': {
            backgroundColor: tokens.color.surface,
          },
        },
        noOptions: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.base,
          color:      tokens.color.textMuted,
          padding:    '10px 14px',
        },
        tag: {
          height:      '20px',
          borderRadius:tokens.radius.full,
        },
      },
    },

    // ── Stepper ────────────────────────────────────────────
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontFamily: tokens.font.family,
          fontSize:   tokens.font.sm,
          fontWeight: tokens.font.medium,
          color:      tokens.color.textMuted,
          '&.Mui-active': {
            color:      tokens.color.dark,
            fontWeight: tokens.font.bold,
          },
          '&.Mui-completed': {
            color: tokens.color.textMuted,
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          width:  '22px',
          height: '22px',
          color:  tokens.color.bg,
          border: `1.5px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.full,
          '&.Mui-active': {
            color:       tokens.color.dark,
            borderColor: tokens.color.yellow,
          },
          '&.Mui-completed': {
            color:       tokens.color.yellow,
            borderColor: tokens.color.yellow,
          },
        },
        text: {
          fontFamily: tokens.font.family,
          fontSize:   '9px',
          fontWeight: tokens.font.extrabold,
        },
      },
    },

    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: tokens.color.border,
          borderWidth: '1px',
        },
      },
    },
  },
})

// ─────────────────────────────────────────────────────────────
// 06. AUGMENTATION TYPESCRIPT
// ─────────────────────────────────────────────────────────────
// Colle ce bloc dans un fichier src/theme.d.ts

/*
import '@mui/material/styles'

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      yellow:      string
      yellowDark:  string
      yellowLight: string
      dark:        string
      mid:         string
      surface:     string
    }
    status: {
      draft:         string
      pending:       string
      toVerify:      string
      critical:      string
      blocked:       string
      submitted:     string
      delivered:     string
      autoValidated: string
    }
  }

  interface PaletteOptions {
    brand?: Palette['brand']
    status?: Palette['status']
  }

  interface Theme {
    tokens: typeof tokens
  }
  interface ThemeOptions {
    tokens?: typeof tokens
  }
}

// Augmenter les variants de Button si nécessaire
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    brand: true
  }
}
*/

// ─────────────────────────────────────────────────────────────
// 07. UTILITAIRES — Status chips helpers
// ─────────────────────────────────────────────────────────────

export type SkipcarStatus =
  | 'draft'
  | 'pending'
  | 'toVerify'
  | 'critical'
  | 'blocked'
  | 'submitted'
  | 'delivered'
  | 'autoValidated'
  | 'notRequired'

export const statusChipProps: Record<
  SkipcarStatus,
  { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' | 'secondary' }
> = {
  draft:         { label: 'Brouillon',    color: 'default'   },
  pending:       { label: 'En cours',     color: 'info'      },
  toVerify:      { label: 'À vérifier',  color: 'primary'   },
  critical:      { label: 'Critique',     color: 'warning'   },
  blocked:       { label: 'Bloqué',       color: 'error'     },
  submitted:     { label: 'Soumis ANTS',  color: 'secondary' },
  delivered:     { label: 'Livré',        color: 'success'   },
  autoValidated: { label: 'Auto-validé',  color: 'success'   },
  notRequired:   { label: 'Non requis',   color: 'default'   },
}

// Scores → couleurs
// Seuils métier Skipcar (stricts) : une extraction IA n'est considérée comme
// fiable qu'à partir de 99 % — c'est le seuil requis pour de l'OCR de documents
// administratifs. Entre 95 et 99 % : à vérifier. En dessous de 95 % : bloqué.
export const SCORE_THRESHOLDS = {
  GREEN: 99,
  ORANGE: 95,
} as const

export function scoreColor(score: number): string {
  if (score >= SCORE_THRESHOLDS.GREEN) return tokens.color.green
  if (score >= SCORE_THRESHOLDS.ORANGE) return tokens.color.orange
  return tokens.color.red
}

export function scoreLabel(score: number): string {
  if (score >= SCORE_THRESHOLDS.GREEN) return 'Auto-validé'
  if (score >= SCORE_THRESHOLDS.ORANGE) return 'À vérifier'
  return 'Bloqué'
}

export function scoreChipColor(score: number): 'success' | 'primary' | 'warning' | 'error' {
  if (score >= SCORE_THRESHOLDS.GREEN) return 'success'
  if (score >= SCORE_THRESHOLDS.ORANGE) return 'warning'
  return 'error'
}

export default skipcarTheme
